import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Checkbox,
    FormControlLabel,
    Chip,
    Stack,
    FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Question, QuestionType } from '../../data/questionnaire';
import { saveQuestionnaire } from '../../services/questionnaireService';
import { v4 as uuidv4 } from 'uuid';

interface Props {
    eventId: string;
    question?: Question;
    allQuestions: Question[];
    onUpdate: (updated: Question[]) => void;
    onClose: () => void;
}

const defaultType: QuestionType = 'SingleAnswer';

const AddEditQuestionModal: React.FC<Props> = ({ eventId, question, allQuestions, onUpdate, onClose }) => {
    const [prompt, setPrompt] = useState(question?.prompt || '');
    const [type, setType] = useState<QuestionType>(question?.type || defaultType);
    const [isRequired, setIsRequired] = useState<boolean>(question?.isRequired || false);
    const [allowMultiAnswer, setAllowMultiAnswer] = useState<boolean>(question?.allowMultiAnswer || false);
    const [options, setOptions] = useState<string[]>(question?.options || []);
    const [newOption, setNewOption] = useState<string>('');
    const [errors, setErrors] = useState<{ prompt?: string; options?: string }>({});

    const validate = () => {
        const newErrors: typeof errors = {};

        const trimmedPrompt = prompt.trim();
        const duplicatePrompt = allQuestions.some(
            q => q.prompt.trim().toLowerCase() === trimmedPrompt.toLowerCase() && q.id !== question?.id
        );

        if (!trimmedPrompt) {
            newErrors.prompt = 'Prompt is required.';
        } else if (duplicatePrompt) {
            newErrors.prompt = 'Prompt must be unique.';
        }

        if (type === 'MultipleChoice' || type === 'SimpleChoice') {
            const uniqueOptions = new Set(options.map(o => o.trim().toLowerCase()));
            if (options.length < 2) {
                newErrors.options = 'At least 2 options are required.';
            } else if (uniqueOptions.size < options.length) {
                newErrors.options = 'All options must be unique.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddOption = () => {
        const trimmed = newOption.trim();
        if (trimmed && !options.some(o => o.trim().toLowerCase() === trimmed.toLowerCase())) {
            setOptions(prev => [...prev, trimmed]);
            setNewOption('');
        }
    };

    const handleDeleteOption = (index: number) => {
        setOptions(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const newQuestion: Question = {
            id: question?.id || uuidv4(),
            prompt: prompt.trim(),
            type,
            isRequired,
            ...(type === 'MultipleChoice' && { allowMultiAnswer: true }),
            ...(type === 'MultipleChoice' || type === 'SimpleChoice' ? { options } : {})
        };

        const updatedQuestions = question
            ? allQuestions.map(q => (q.id === question.id ? newQuestion : q))
            : [...allQuestions, newQuestion];

        try {
            const saved = await saveQuestionnaire(eventId, {
                eventId,
                questions: updatedQuestions,
            });

            onUpdate(saved.questions);
            onClose();
        } catch (error) {
            console.error('Error saving questionnaire:', error);
            alert('Failed to save questionnaire. Please try again.');
        }
    };

    useEffect(() => {
        if (type === 'MultipleChoice') {
            setAllowMultiAnswer(true);
        } else {
            setAllowMultiAnswer(false);
        }

        if (type !== 'MultipleChoice' && type !== 'SimpleChoice') {
            setOptions([]);
        }
    }, [type]);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{question ? 'Edit Question' : 'Add Question'}</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    margin="normal"
                    error={!!errors.prompt}
                    helperText={errors.prompt}
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={type}
                        label="Type"
                        onChange={(e) => setType(e.target.value as QuestionType)}
                    >
                        <MenuItem value="SingleAnswer">Single Answer</MenuItem>
                        <MenuItem value="MultipleChoice">Multiple Choice</MenuItem>
                        <MenuItem value="SimpleChoice">Simple Choice</MenuItem>
                        <MenuItem value="TrueFalse">True / False</MenuItem>
                        <MenuItem value="MultiLineText">Multi-line Text</MenuItem>
                    </Select>
                </FormControl>

                {(type === 'MultipleChoice' || type === 'SimpleChoice') && (
                    <Box mt={2}>
                        <TextField
                            label="Add Option"
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddOption();
                                }
                            }}
                            fullWidth
                        />
                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                            {options.map((opt, i) => (
                                <Chip
                                    key={i}
                                    label={opt}
                                    onDelete={() => handleDeleteOption(i)}
                                    deleteIcon={<DeleteIcon sx={{ color: '#d32f2f' }} />}
                                    sx={{
                                        backgroundColor: '#e0e0e0 !important',
                                        color: '#555 !important',
                                        fontWeight: 'bold',
                                        height: '40px',
                                        fontSize: '1rem',
                                        px: 2,
                                        '& .MuiChip-deleteIcon': {
                                            color: '#d32f2f',
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                        {errors.options && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {errors.options}
                            </FormHelperText>
                        )}
                    </Box>
                )}

                {type === 'MultipleChoice' && (
                    <FormControlLabel
                        control={<Checkbox checked={allowMultiAnswer} disabled />}
                        label="Allow multiple answers"
                    />
                )}

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isRequired}
                            onChange={(e) => setIsRequired(e.target.checked)}
                        />
                    }
                    label="Required"
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    sx={{ backgroundColor: '#d32f2f !important', color: '#fff !important' }}
                    variant="contained"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    sx={{ backgroundColor: '#2e7d32 !important', color: '#fff !important' }}
                    variant="contained"
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditQuestionModal;